import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import MarginField from '@/components/margin/MarginField';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useMspBaseCatalogue, useUpdateFilterPack } from '@/services/api/hooks/catalog';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { CatalogueProvider } from '@/services/catalogue/CatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const ManageBaseCatalogueGlobalMarginModalFields = z.object({
  value: z.string()
});

type ManageBaseCatalogueGlobalMarginModalFields = z.infer<typeof ManageBaseCatalogueGlobalMarginModalFields>;

export const ManageBaseCatalogueGlobalMarginModal: FC<ModalProps<{ mspId: string }>> = props => {
  const role = useRole();

  const ManageBaseCatalogueGlobalMarginForm = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing msp id');

    const [baseCatalogue] = useMspBaseCatalogue(mspId);

    const updateFilterPack = useUpdateFilterPack();

    const methods = useForm<ManageBaseCatalogueGlobalMarginModalFields>({
      resolver: zodResolver(ManageBaseCatalogueGlobalMarginModalFields),
      defaultValues: { value: String(baseCatalogue.filterPack.globalMarginModifier) }
    });

    const onSubmit = async (data: ManageBaseCatalogueGlobalMarginModalFields) => {
      await updateFilterPack.mutateAsync({
        ...baseCatalogue.filterPack,
        marginFilterIds: baseCatalogue.filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: baseCatalogue.filterPack.productFilters.map(filter => filter.id),
        globalMarginModifier: Number(data.value)
      });
      toast.success('Successfully updated global margin');
      props.close();
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-col gap-4 py-2">
        <div className="text-sm">
          <p>
            <strong>To apply a DISCOUNT:</strong> Enter a negative number (e.g., -10 for 10% off).
          </p>
          <p>
            <strong>To apply a SURCHARGE:</strong> Enter a positive number (e.g., 10 for a 10% markup).
          </p>
        </div>
        <MarginField id="value" />
        <Button
          loading={updateFilterPack.isLoading}
          className="w-fit place-self-end"
          variant={ButtonVariant.Primary}
          type="submit"
        >
          Update
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit MSP Wholesale Discount / Surcharge</h1>
        </Modal.Controls>
        <div className="mt-4 mb-4 bg-blue-50 text-blue-900 p-4 rounded-md border border-blue-200 text-sm">
          This modifier applies directly to the MSP's Base Catalog. It alters the underlying wholesale cost of all products before the MSP applies their own margins.
        </div>

        <Suspense fallback={<Loading />}>
          <CatalogueProvider configuration={{ for: RoleType.Admin, as: role.type }}>
            <ManageBaseCatalogueGlobalMarginForm />
          </CatalogueProvider>
        </Suspense>
      </Card>
    </Modal>
  );
};
